import { Injectable, inject } from "@angular/core";
import { StringHelper } from "@tylertech/shared-module";
import { IFormMetadata, IViewMetadata, ProfileSettingViewModel, ProfileViewModel } from "@tylertech/tyler-api-types";
import { ISelectOption } from "@tylertech/forge";
import { UserProfileService } from "@tylertech/core-module";

@Injectable({ providedIn: "root" })
export class ViewShortcutService
{
    private $userProfileService = inject(UserProfileService);

    ShortcutViews: ISelectOption[] = [];

    private contextMenuHandlers = new WeakMap<HTMLElement, EventListener>();

    LoadShortcuts(formMetadata: IFormMetadata): void
    {
        this.$userProfileService.GetFormShortcuts(formMetadata.FormId).then((result: string[]) =>
        {
            this.ShortcutViews = [];
            if (result)
            {
                const viewPaths = formMetadata.Views.map(x =>
                {
                    return { Path: `${x.Path ? `${x.Path}/` : ""}${x.Title}`, Title: x.Title };
                });
                result.forEach(r =>
                {
                    const v = viewPaths.find(x => x.Path === r);
                    this.addShortcutView(<ISelectOption>{ value: v.Path, label: v.Title });
                });
            }
        });
    }

    OnContextMenu(event: MouseEvent, view: IViewMetadata, formId: string): void
    {
        const self = this;
        event.preventDefault();
        const openPopovers = document.getElementsByTagName("forge-popover");
        for (let i = 0, l = openPopovers.length; i < l; i++)
        {
            if (openPopovers[i].open)
                document.body.removeChild(openPopovers[i]);
        }

        // Popover
        const popover = document.createElement("forge-popover");
        popover.anchorElement = event.target as HTMLElement;
        popover.placement = "bottom";
        popover.positionStrategy = "fixed";

        // List
        const optionList = document.createElement("forge-list");
        optionList.dense = true;

        popover.appendChild(optionList);

        const contextMenuItem =
        {
            display: (self: ViewShortcutService, view: IViewMetadata) =>
            {
                if (self.IsShortcut(view))
                    return "Remove from shortcuts";
                return "Add to shortcuts";
            },
            value: "addShortcut",
            onClick: (self: ViewShortcutService, view: IViewMetadata) =>
            {
                self.UpdateShortcuts(view, formId);
            },
        };

        const execute = (): void =>
        {
            contextMenuItem.onClick(this, view);
        };

        const button = document.createElement("button");
        button.type = "button";
        button.innerText = contextMenuItem.display(this, view);
        button.addEventListener("click", execute);
        this.contextMenuHandlers.set(button, execute);
        const li = document.createElement("forge-list-item");
        li.appendChild(button);
        const listItem = optionList.appendChild(li);

        const listItemHandler = (): void => closePopover();
        listItem.addEventListener("click", listItemHandler);
        this.contextMenuHandlers.set(listItem, listItemHandler);

        const closePopover = (): void =>
        {
            const allButtons = popover.querySelectorAll("button");
            allButtons.forEach(btn =>
            {
                const handler = self.contextMenuHandlers.get(btn);
                if (handler)
                {
                    btn.removeEventListener("click", handler);
                    self.contextMenuHandlers.delete(btn);
                }
            });

            const allListItems = popover.querySelectorAll("forge-list-item");
            allListItems.forEach(item =>
            {
                const handler = self.contextMenuHandlers.get(item as HTMLElement);
                if (handler)
                {
                    (item as HTMLElement).removeEventListener("click", handler);
                    self.contextMenuHandlers.delete(item as HTMLElement);
                }
            });
            popover.removeEventListener("forge-popover-toggle", closePopover);
            popover.open = false;
        };

        document.body.appendChild(popover);
        popover.open = true;
        popover.addEventListener("forge-popover-toggle", closePopover);
    }

    protected contextMenu(_: unknown): void
    {
        requestAnimationFrame(() =>
        {
            (document.querySelector("forge-popover forge-list forge-list-item button") as HTMLElement).focus();
        });
    }

    protected UpdateShortcuts(view: IViewMetadata, formId: string): void
    {
        const viewPath = `${view.Path ? `${view.Path}/` : ""}${view.Title}`;
        const option: ISelectOption = { value: viewPath, label: view.Title };
        if (this.IsShortcut(view))
            this.ShortcutViews.splice(this.ShortcutViews.map(x => x.value).indexOf(viewPath), 1);
        else
            this.addShortcutView(option);

        if (!this.ShortcutViews || this.ShortcutViews.length === 0)
            this.$userProfileService.Clear(this.buildProfile(formId));
        else
            this.$userProfileService.Save(this.buildProfile(formId));
    }

    protected addShortcutView(option: ISelectOption): void
    {
        option["$baseLabel"] = option.label;
        this.ShortcutViews.push(option);
        const shortcuts = this.ShortcutViews.filter(x => x["$baseLabel"] === option["$baseLabel"]);
        if (shortcuts.length > 1)
        {
            shortcuts.forEach((x, i) => shortcuts[i].label = x.value.replace(/\//g, " | "));
        }
    }

    protected IsShortcut(view: IViewMetadata): boolean
    {
        const viewPath = `${view.Path ? `${view.Path}/` : ""}${view.Title}`;
        return this.ShortcutViews.map(v => v.value).indexOf(viewPath) > -1;
    }

    protected buildProfile(formId: string): ProfileViewModel
    {
        const pSettings: Array<ProfileSettingViewModel> =
            [
                {
                    Id: `ViewShortcuts.${formId}`,
                    Value: StringHelper.stringify(this.ShortcutViews.map(x => x.value))
                }
            ];
        return {
            Id: undefined,
            Type: undefined,
            Settings: pSettings,
        };
    }
}
